import parseUrlToQueryParams from "@/v2/utils/parseUrlToQueryParams";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import LoadingAnimation from "../ui/LoadingAnimation";
import FindAllExpiredIqamaNotifications from "@/v2/api/notifications/find-all/Iqama";
import FindAllExpiredPlantIdNotifications from "@/v2/api/notifications/find-all/PlantId";
import NotificationCard from "./Card";

export default function NotificationContent() {
    const [searchParams, _setSearchParams] = useSearchParams();
    const queryParams = parseUrlToQueryParams(searchParams);

    const { skip, limit, search, filter } = queryParams;

    const { refetch: refetchIqama, ...iqamaQuery } = useQuery({
        queryKey: ["iqama-notifications", skip, limit, search, filter],
        queryFn: () =>
            FindAllExpiredIqamaNotifications({
                limit: limit ?? 20,
                skip: skip ?? 0,
                search,
                filter,
            }),
        refetchOnMount: true,
    });

    const { refetch: refetchPlantId, ...plantIdQuery } = useQuery({
        queryKey: ["plant-id-notifications", skip, limit, search, filter],
        queryFn: () =>
            FindAllExpiredPlantIdNotifications({
                limit: limit ?? 20,
                skip: skip ?? 0,
                search,
                filter,
            }),
        refetchOnMount: true,
    });

    if (iqamaQuery?.isLoading || plantIdQuery?.isLoading) {
        return <LoadingAnimation />;
    }

    const allIqamaEmpty =
        !iqamaQuery?.data ||
        Object.values(iqamaQuery.data).every(
            (arr) => !arr || (Array.isArray(arr) && arr.length === 0),
        );

    const allPlantIdEmpty =
        !plantIdQuery?.data ||
        Object.values(plantIdQuery.data).every(
            (arr) => !arr || (Array.isArray(arr) && arr.length === 0),
        );

    const noNotifications = allIqamaEmpty && allPlantIdEmpty;

    if (noNotifications) {
        return (
            <div className="flex h-40 items-center justify-center text-gray-500">
                No new notifications
            </div>
        );
    }

    const renderNotifications = (
        data: any,
        alertExpiryType: "IQAMA_EXPIRY" | "PLANT_ID_EXPIRY",
    ) => (
        <>
            {data?.EXPIRED?.map((notification: any) => (
                <NotificationCard
                    employeeDetails={notification}
                    key={notification.id}
                    category="expired"
                    alertExpiryType={alertExpiryType}
                />
            ))}
            {data?.EXPIRED_TODAY?.map((notification: any) => (
                <NotificationCard
                    employeeDetails={notification}
                    key={notification.id}
                    category="expired_today"
                    alertExpiryType={alertExpiryType}
                />
            ))}
            {Array.from({ length: 10 }, (_, i) => i + 1).map((day) =>
                data?.[`EXPIRES_${day}_DAY`]?.map((notification: any) => (
                    <NotificationCard
                        employeeDetails={notification}
                        key={notification.id}
                        category={
                            `expires_${day}_day` as
                                | "expires_1_day"
                                | "expires_2_day"
                                | "expires_3_day"
                                | "expires_4_day"
                                | "expires_5_day"
                                | "expires_6_day"
                                | "expires_7_day"
                                | "expires_8_day"
                                | "expires_9_day"
                                | "expires_10_day"
                        }
                        alertExpiryType={alertExpiryType}
                    />
                )),
            )}
        </>
    );

    return (
        <div>
            {/* IQAMA EXPIRY */}
            {renderNotifications(iqamaQuery?.data, "IQAMA_EXPIRY")}

            {/* PLANT ID EXPIRY */}
            {renderNotifications(plantIdQuery?.data, "PLANT_ID_EXPIRY")}
        </div>
    );
}
